import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLoginWithSms } from '@privy-io/react-auth'
import OtpInput from 'react-otp-input'
import { Button, Icon, Header } from '../components/ui'
import { useStore } from '../store'

export function OTPScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const phoneNumber = location.state?.phoneNumber || ''
  const otpContainerRef = useRef<HTMLDivElement>(null)

  const [code, setCode] = useState(import.meta.env.DEV ? '573956' : '')
  const [error, setError] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(30)

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  // Scroll input into view when keyboard appears (viewport resizes)
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const handleResize = () => {
      const activeInput = otpContainerRef.current?.querySelector('input:focus')
      if (activeInput) {
        activeInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [])

  const { setPhone, setAuthenticated } = useStore()

  const { loginWithCode, sendCode, state } = useLoginWithSms({
    onComplete: (user) => {
      // Sync to Zustand store
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const privUser = user as any
      setPhone(privUser.phone?.number || phoneNumber)
      setAuthenticated(true)
      navigate('/capture')
    },
    onError: (error) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError((error as any).message || 'Failed to verify code. Please try again.')
    },
  })

  // Fallback: also watch state for 'done' status
  useEffect(() => {
    if (state.status === 'done') {
      setAuthenticated(true)
      navigate('/capture')
    }
  }, [state.status, navigate, setAuthenticated])

  const isSubmitting = state.status === 'submitting-code'
  const isResending = state.status === 'sending-code'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (code.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    loginWithCode({ code })
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    setError(null)
    setCode('')
    sendCode({ phoneNumber })
    setResendCooldown(30)
  }

  const handleBack = () => {
    navigate('/', { state: { fromOtp: true }, replace: true })
  }

  // Format phone for display (mask middle digits)
  const formatPhoneDisplay = (phone: string) => {
    if (phone.length < 4) return phone
    const last4 = phone.slice(-4)
    return `***${last4}`
  }

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header showLogo />

      {/* Main Content - Centered */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 w-full max-w-md mx-auto">
        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Icon name="lock" size={32} className="text-[var(--color-secondary)]" />
          </div>
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            Enter verification code
          </h2>
          <p className="text-[var(--color-text-muted)] text-sm">
            We sent a 6-digit code to {formatPhoneDisplay(phoneNumber)}
          </p>
        </div>

        {/* OTP Form */}
        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="w-full" ref={otpContainerRef}>
            <OtpInput
              value={code}
              onChange={(value) => {
                setCode(value)
                setError(null)
              }}
              numInputs={6}
              shouldAutoFocus
              inputType="number"
              renderInput={(props) => (
                <input 
                  {...props} 
                  className="focus:border-[var(--color-primary)] transition-colors"
                />
              )}
              containerStyle="flex justify-between gap-2"
              inputStyle={{
                width: '100%',
                height: '56px',
                fontSize: '1.5rem',
                fontWeight: 600,
                fontFamily: 'var(--font-family)',
                borderRadius: '0.75rem',
                border: `2px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
                background: 'var(--color-surface)',
                color: 'var(--color-text)',
                outline: 'none',
                textAlign: 'center',
              }}
            />
            {error && (
              <p className="mt-2 text-sm text-[var(--color-error)] text-center">{error}</p>
            )}
          </div>

          <Button type="submit" disabled={isSubmitting || code.length !== 6}>
            {isSubmitting ? 'Verifying...' : 'Verify'}
            {!isSubmitting && <Icon name="arrow-right" size={20} />}
          </Button>
        </form>

        {/* Resend option */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[var(--color-text-muted)] mb-2">
            Didn't receive the code?
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className="text-sm font-semibold text-[var(--color-secondary)] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </div>

        {/* Back to change number */}
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={handleBack}
            className="text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <Icon name="arrow-left" size={16} className="inline mr-1" />
            Use a different phone number
          </button>
        </div>
      </main>

      {/* Safe area spacer for bottom */}
      <div className="h-8 flex-shrink-0" />
    </div>
  )
}
